import React, { useState, useMemo } from 'react';

function GuidedDesignArtScreen({ designAddOns, nailArtAddOns, selectedAddOns, onToggleAddOn, parentServiceId, serviceName, onSkip }) {
  const [selectedLevel, setSelectedLevel] = useState(null); // 'simple', 'intermediate', 'detailed'
  const [nailArtExpanded, setNailArtExpanded] = useState(false);

  const formatPrice = (price) => price > 0 ? `+$${price}` : 'Free';

  // Group nail art add-ons into levels
  const nailArtLevels = useMemo(() => {
    const levels = {};
    for (const addOn of nailArtAddOns) {
      const name = (addOn.name || '').toLowerCase();
      let level = null;
      let variant = null;

      // Match roman numerals as whole tokens (e.g. "nail art iii" or "level i - accent")
      // Using word boundaries to avoid matching 'i' inside words like "finish" or "glitter"
      if (name.includes('iii') || name.includes('detailed') || /\blevel\s*3\b/.test(name)) {
        level = 'detailed';
        variant = 'full'; // Detailed has no accent/set split
      } else if (name.includes(' ii') || name.includes('intermediate') || /\blevel\s*2\b/.test(name)) {
        level = 'intermediate';
        variant = name.includes('accent') ? 'accent' : 'set';
      } else if (/\bi\b/.test(name) || name.includes('simple') || /\blevel\s*1\b/.test(name)) {
        level = 'simple';
        variant = name.includes('accent') ? 'accent' : 'set';
      }

      if (level) {
        if (!levels[level]) levels[level] = { addOns: [], accent: null, set: null, full: null };
        levels[level].addOns.push(addOn);
        if (variant === 'accent') levels[level].accent = addOn;
        else if (variant === 'set') levels[level].set = addOn;
        else levels[level].full = addOn;
      }
    }
    return levels;
  }, [nailArtAddOns]);

  const levelOrder = [
    { key: 'simple', label: 'SIMPLE' },
    { key: 'intermediate', label: 'INTERMEDIATE' },
    { key: 'detailed', label: 'DETAILED' },
  ];

  // Check if any nail art is currently selected
  const selectedNailArt = nailArtAddOns.find(a =>
    selectedAddOns.some(s => s.id === a.id && s.parentServiceId === parentServiceId)
  );

  function handleDesignSelect(addOn) {
    onToggleAddOn(addOn, parentServiceId);
  }

  function handleLevelSelect(levelKey) {
    // Deselect any currently selected nail art
    if (selectedNailArt) {
      onToggleAddOn(selectedNailArt, parentServiceId);
    }

    const level = nailArtLevels[levelKey];
    if (!level) return;

    setNailArtExpanded(true);
    // If detailed (no accent/set), auto-select it
    if (levelKey === 'detailed' && level.full) {
      setSelectedLevel('detailed');
      onToggleAddOn(level.full, parentServiceId);
    } else if (levelKey === 'detailed' && level.addOns.length === 1) {
      setSelectedLevel('detailed');
      onToggleAddOn(level.addOns[0], parentServiceId);
    } else {
      setSelectedLevel(levelKey);
    }
  }

  function handleVariantSelect(addOn) {
    // Deselect any currently selected nail art
    if (selectedNailArt && selectedNailArt.id !== addOn.id) {
      onToggleAddOn(selectedNailArt, parentServiceId);
    }
    const isSelected = selectedAddOns.some(s => s.id === addOn.id && s.parentServiceId === parentServiceId);
    if (!isSelected) {
      onToggleAddOn(addOn, parentServiceId);
    }
  }

  function handleChangeLevel() {
    // Deselect any currently selected nail art
    if (selectedNailArt) {
      onToggleAddOn(selectedNailArt, parentServiceId);
    }
    setSelectedLevel(null);
  }

  // Fallback descriptions for common finishes
  const finishDescriptions = {
    'french': 'Classic white-tip design with a natural base',
    'chrome': 'Mirror-like metallic reflective finish',
    'cat eye': 'Magnetic polish that creates a striking light effect',
    'gel': 'High-shine, chip-resistant finish that lasts up to 2 weeks',
    'shellac': 'Hybrid gel-polish for a glossy, long-lasting finish',
    'matte': 'Smooth, velvety non-shine top coat',
    'ombre': 'Beautiful gradient blend of two or more colours',
    'glitter': 'Sparkle and shimmer added to your nails',
  };

  function getFinishDescription(addOn) {
    if (addOn.description) return addOn.description;
    const name = (addOn.name || '').toLowerCase();
    for (const [key, desc] of Object.entries(finishDescriptions)) {
      if (name.includes(key)) return desc;
    }
    return '';
  }

  const hasDesign = designAddOns.length > 0;
  const hasNailArt = nailArtAddOns.length > 0;
  const hasGroupedLevels = Object.keys(nailArtLevels).length > 0;

  return (
    <div className="guided-screen-content">
      {hasDesign && (
        <div className="guided-section">
          <div className="guided-section-title">Finishes</div>
          <div className="guided-design-grid">
            {designAddOns.map(addOn => {
              const isSelected = selectedAddOns.some(s => s.id === addOn.id && s.parentServiceId === parentServiceId);
              return (
                <div
                  key={addOn.id}
                  className={`guided-design-card ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleDesignSelect(addOn)}
                >
                  <div className="guided-design-card-name">{addOn.name}</div>
                  {getFinishDescription(addOn) && <div className="guided-design-card-desc">{getFinishDescription(addOn)}</div>}
                  <div className="guided-design-card-price">{formatPrice(addOn.price)}</div>
                  {addOn.duration > 0 && <div className="guided-card-time">+{addOn.duration} min</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {hasNailArt && hasGroupedLevels && (
        <div className="guided-section">
          <button
            className={`guided-nail-art-toggle ${nailArtExpanded || selectedNailArt ? 'open' : ''}`}
            onClick={() => setNailArtExpanded(!nailArtExpanded)}
          >
            <span>Nail Art</span>
            <span className="toggle-arrow">▼</span>
          </button>

          {/* Step 1: Pick a level */}
          {(nailArtExpanded || selectedNailArt) && !selectedLevel && (
            <div className="guided-card-list">
              {levelOrder.map(({ key, label }) => {
                const level = nailArtLevels[key];
                if (!level) return null;
                // Show price range
                const prices = level.addOns.map(a => a.price).filter(p => p > 0);
                const minPrice = Math.min(...prices);
                const maxPrice = Math.max(...prices);
                const priceStr = minPrice === maxPrice ? formatPrice(minPrice) : `${formatPrice(minPrice)}–$${maxPrice}`;
                const durations = level.addOns.map(a => a.duration).filter(d => d > 0);
                const minDur = Math.min(...durations);
                const maxDur = Math.max(...durations);
                const durStr = minDur === maxDur ? `+${minDur} min` : `+${minDur}–${maxDur} min`;
                const desc = level.addOns[0]?.description || '';
                return (
                  <div
                    key={key}
                    className="guided-card"
                    onClick={() => handleLevelSelect(key)}
                  >
                    <div className="guided-card-left">
                      <div className="guided-card-name">{label}</div>
                      {desc && <div className="guided-card-desc">{desc}</div>}
                    </div>
                    <div className="guided-card-right">
                      <div className="guided-card-meta">
                        <div className="guided-card-price">{priceStr}</div>
                        {durations.length > 0 && <div className="guided-card-time">{durStr}</div>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Step 2: Pick accent or set (if applicable) */}
          {(nailArtExpanded || selectedNailArt) && selectedLevel && selectedLevel !== 'detailed' && nailArtLevels[selectedLevel] && (
            <>
              <div className="guided-yesno-collapsed selected-yes" onClick={handleChangeLevel}>
                <span className="guided-yesno-collapsed-check">✓</span>
                <span className="guided-yesno-collapsed-text">
                  {selectedLevel === 'simple' ? 'Simple' : 'Intermediate'} Nail Art
                </span>
                <span className="guided-yesno-collapsed-change">Change</span>
              </div>
              <div className="guided-section-title" style={{ marginTop: 10 }}>Accent or Full Set?</div>
              <div className="guided-yesno-grid">
                {nailArtLevels[selectedLevel].accent && (() => {
                  const addOn = nailArtLevels[selectedLevel].accent;
                  const isSelected = selectedAddOns.some(s => s.id === addOn.id && s.parentServiceId === parentServiceId);
                  return (
                    <button
                      className={`guided-yesno-btn ${isSelected ? 'guided-variant-selected' : ''}`}
                      onClick={() => handleVariantSelect(addOn)}
                    >
                      <div className="guided-yesno-label">Accent</div>
                      <div className="guided-yesno-sub">2–4 nails</div>
                      <div className="guided-variant-price">{formatPrice(addOn.price)}</div>
                      {addOn.duration > 0 && <div className="guided-card-time">+{addOn.duration} min</div>}
                    </button>
                  );
                })()}
                {nailArtLevels[selectedLevel].set && (() => {
                  const addOn = nailArtLevels[selectedLevel].set;
                  const isSelected = selectedAddOns.some(s => s.id === addOn.id && s.parentServiceId === parentServiceId);
                  return (
                    <button
                      className={`guided-yesno-btn ${isSelected ? 'guided-variant-selected' : ''}`}
                      onClick={() => handleVariantSelect(addOn)}
                    >
                      <div className="guided-yesno-label">Full Set</div>
                      <div className="guided-yesno-sub">5–10 nails</div>
                      <div className="guided-variant-price">{formatPrice(addOn.price)}</div>
                      {addOn.duration > 0 && <div className="guided-card-time">+{addOn.duration} min</div>}
                    </button>
                  );
                })()}
              </div>
            </>
          )}

          {/* Detailed: show collapsed pill (auto-selected) */}
          {(nailArtExpanded || selectedNailArt) && selectedLevel === 'detailed' && (
            <div className="guided-yesno-collapsed selected-yes" onClick={handleChangeLevel}>
              <span className="guided-yesno-collapsed-check">✓</span>
              <span className="guided-yesno-collapsed-text">Detailed Nail Art</span>
              <span className="guided-yesno-collapsed-change">Change</span>
            </div>
          )}
        </div>
      )}

      {/* Fallback: if nail art add-ons couldn't be grouped, show them flat */}
      {hasNailArt && !hasGroupedLevels && (
        <div className="guided-section">
          <div className="guided-section-title">Nail Art</div>
          <div className="guided-card-list">
            {nailArtAddOns.map(addOn => {
              const isSelected = selectedAddOns.some(s => s.id === addOn.id && s.parentServiceId === parentServiceId);
              return (
                <div
                  key={addOn.id}
                  className={`guided-card ${isSelected ? 'selected' : ''}`}
                  onClick={() => {
                    if (selectedNailArt && selectedNailArt.id !== addOn.id) {
                      onToggleAddOn(selectedNailArt, parentServiceId);
                    }
                    onToggleAddOn(addOn, parentServiceId);
                  }}
                >
                  <div className="guided-card-left">
                    <div className="guided-card-name">{addOn.name}</div>
                    {addOn.description && <div className="guided-card-desc">{addOn.description}</div>}
                  </div>
                  <div className="guided-card-right">
                    <div className="guided-card-meta">
                      <div className="guided-card-price">{formatPrice(addOn.price)}</div>
                      {addOn.duration > 0 && <div className="guided-card-time">+{addOn.duration} min</div>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <button className="guided-no-addons-btn" onClick={onSkip}>
        NO ADD-ONS NEEDED
      </button>
    </div>
  );
}

export default GuidedDesignArtScreen;
